/**
 * Docs landing (`/docs`) — in-app developer docs hub.
 *
 * Same shape as Home's "fresh" Quickstart but persistent, with
 * deeper explanation and all-language snippets. Three columns on
 * desktop: TOC, content, Try-it. `05-pages.md` §5c.
 */
import { CodeSnippet } from '@/components/primitives/CodeSnippet';
import { RunItPanel } from '@/components/primitives/RunItPanel';
import { Island } from '@/components/shell/Island';
import { Button } from '@/components/ui/button';
import { useKeys } from '@/lib/queries';
import {
  ArrowRight,
  BookOpen,
  Code,
  Lightning,
  Package,
  WebhooksLogo,
} from '@phosphor-icons/react';
import { Link, createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';

export const Route = createFileRoute('/_app/docs/')({
  component: DocsLanding,
});

const CHAPTERS = [
  {
    href: '/docs/quickstart',
    icon: Lightning,
    label: 'Quickstart',
    blurb: 'Mint a key, POST an audit, poll until complete. End-to-end in 3 minutes.',
  },
  {
    href: '/docs/sdks',
    icon: Package,
    label: 'SDKs',
    blurb: 'Official clients for Node, Python, and Go. First-call samples included.',
  },
  {
    href: '/docs/api',
    icon: Code,
    label: 'API reference',
    blurb: 'Every public endpoint, with request shape, response, and error codes.',
  },
  {
    href: '/docs/webhooks',
    icon: WebhooksLogo,
    label: 'Webhooks',
    blurb: 'Payload shape, signature verification, replay semantics.',
  },
  {
    href: '/docs/changelog',
    icon: BookOpen,
    label: 'Changelog',
    blurb: 'What shipped, in reverse chronological order.',
  },
] as const;

function DocsLanding(): ReactElement {
  const keys = useKeys();
  const firstKeyPrefix = keys.data?.[0]?.prefix;

  return (
    <Island breadcrumb={[{ label: 'Docs' }]}>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="space-y-6">
          <header className="space-y-2">
            <h1 className="text-2xl font-semibold text-[var(--color-ink)]">
              Developer documentation
            </h1>
            <p className="text-sm text-[var(--color-mute)]">
              Everything you need to integrate ClickWeave into your pipeline. All samples auto-fill
              with your default API key when you pick one.
            </p>
          </header>

          <section className="grid gap-3 sm:grid-cols-2">
            {CHAPTERS.map((c) => (
              <Link
                key={c.href}
                to={c.href}
                className="group flex items-start gap-3 rounded-xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4 transition-colors hover:border-[var(--color-accent-brand)]/60 hover:bg-[var(--color-paper-alt)]/40"
              >
                <c.icon
                  aria-hidden="true"
                  size={24}
                  weight="duotone"
                  className="text-[var(--color-accent-brand)]"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-1 text-sm font-semibold text-[var(--color-ink)]">
                    {c.label}
                    <ArrowRight
                      aria-hidden="true"
                      size={14}
                      className="transition-transform group-hover:translate-x-0.5"
                    />
                  </div>
                  <p className="mt-0.5 text-xs text-[var(--color-mute)]">{c.blurb}</p>
                </div>
              </Link>
            ))}
          </section>

          <section className="space-y-3 rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-5">
            <h2 className="text-base font-semibold text-[var(--color-ink)]">First call</h2>
            <p className="text-sm text-[var(--color-mute)]">
              The snippet below POSTs a one-off audit. Paste it into your terminal or run it in-app
              from the right column.
            </p>
            <CodeSnippet
              code={
                'curl https://api.clickweave.co/v1/audits \\\n' +
                "  -H 'Authorization: Bearer $API_KEY' \\\n" +
                "  -H 'Content-Type: application/json' \\\n" +
                '  -d \'{"url":"https://example.com"}\''
              }
              variables={{ API_KEY: firstKeyPrefix ?? '<YOUR_API_KEY>' }}
            />
          </section>
        </div>

        <aside className="space-y-4">
          <section className="rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
            <div className="mb-2 text-xs font-medium uppercase tracking-[0.06em] text-[var(--color-mute)]">
              Try it
            </div>
            <RunItPanel apiKey={firstKeyPrefix} languages={['curl']} />
            <div className="mt-3 text-xs text-[var(--color-mute)]">
              Runs against your workspace using your Clerk session. Create a key in{' '}
              <Link to="/settings/keys" className="underline">
                API keys
              </Link>{' '}
              to run outside the dashboard.
            </div>
          </section>
          <section className="rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-4">
            <div className="mb-2 text-xs font-medium uppercase tracking-[0.06em] text-[var(--color-mute)]">
              Need help?
            </div>
            <p className="text-sm text-[var(--color-mute)]">
              The{' '}
              <Link to="/help" className="underline">
                Help page
              </Link>{' '}
              has a direct email and keyboard shortcut reference.
            </p>
          </section>
        </aside>
      </div>
    </Island>
  );
}
